import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ColumnDef } from '@tanstack/react-table';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../lib/api';
import { Badge, Button } from '../atoms';
import type { SelectOption } from '../atoms';
import { PageHeader } from '../shell';
import { DataTable, Pagination, ListToolbar, SavedViewMenu } from '../organisms';
import { useServerList } from '../lib/useServerList';

/**
 * ClientsList — /v2/clients
 *
 * Lists every client in the org with status pill + setup quick stats.
 * Clicking a row opens the client detail page.
 *
 * Server-side search / sort / paginate (PRD 06.1): the debounced search box,
 * the status + state filters, the sortable column headers, and the pager all
 * drive the generic-CRUD list endpoint
 * (`GET /api/clients?q&status&state&sort&dir&page&limit`), which enforces
 * org + SoD row-scope and returns exactly the page shown. `?q=` LIKE-matches
 * name/short_name/city/state/category/website_url (enums.js SEARCH_COLUMNS) so
 * typing "AL" / "Birmingham" / "Food & Beverage" surfaces the matching
 * businesses. No client-side filtering or slicing happens here.
 *
 * Saved views (PRD 2.5): the SavedViewMenu persists & restores the full
 * filter/sort envelope ({ q, status, state, sort, dir }) against the real
 * /api/saved-views store; applying a view writes back onto this list's
 * server-side state.
 *
 * Phase 11 Block 7 PR 2 (2026-05-12); rewired for server-side list (06.1);
 * granular search + saved views (2.3 / 2.5).
 */

interface Client {
  id: string;
  name: string;
  short_name: string | null;
  status: 'active' | 'inactive' | 'in_setup' | 'historical';
  type: string | null;
  category: string | null;
  website_url: string | null;
  state: string | null;
  city: string | null;
}

const statusVariant: Record<Client['status'], 'success' | 'warning' | 'neutral' | 'info'> = {
  active: 'success',
  inactive: 'neutral',
  in_setup: 'warning',
  historical: 'info',
};

const statusLabel: Record<Client['status'], string> = {
  active: 'Active',
  inactive: 'Inactive',
  in_setup: 'In Setup',
  historical: 'Historical',
};

const STATUS_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
  { value: 'in_setup', label: 'In Setup' },
  { value: 'historical', label: 'Historical' },
];

// Roles that may create org-scoped ("shared") saved views (admin-tier, matching
// the saved-views router's ADMIN_TIER_LEVEL). Also gates the shared checkbox.
const ADMIN_TIER = new Set(['super_admin', 'admin', 'administrator']);

interface CurrentUser { id: string; role: string }

interface SchemaField { name: string; enumOptions?: SelectOption[] }
interface SchemaResponse { fields: SchemaField[] }

export function ClientsList() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  // Deep-link filters, read once at mount so a dashboard drill-down lands on
  // exactly the set it counted. This page is NOT a GenericList (it owns bespoke
  // filters and saved views), so it needs its own reader — without it, the
  // sector donut linked here and opened the FULL register, which is the fake
  // affordance this whole pass is about.
  const [statusFilter, setStatusFilter] = useState(() => searchParams.get('status') ?? '');
  const [stateFilter, setStateFilter] = useState(() => searchParams.get('state') ?? '');
  // B6 (Pepco Jul-15): slice the client list by the firm's own client vocabulary —
  // "Commercial", "BPP" and the rest. TWO separate cuts because the schema keeps two
  // separate columns, each with its own org-settings registry: `type` (the kind of
  // client — Commercial / Retail / Industrial …) and `category` (the firm's servicing
  // tier — standard / priority / key_account …). Both are server-side equality via the
  // crud SCALAR_FILTERS, same path as status/state.
  const [typeFilter, setTypeFilter] = useState(() => searchParams.get('type') ?? '');
  const [categoryFilter, setCategoryFilter] = useState(() => searchParams.get('category') ?? '');

  const list = useServerList<Client>({
    resource: 'clients',
    queryKey: ['clients-list'],
    defaultPageSize: 25,
    defaultSort: { id: 'name', desc: false },
    filters: { status: statusFilter, state: stateFilter, type: typeFilter, category: categoryFilter },
  });

  // Options come from the org's OWN registries (org-settings client-types /
  // client-categories), not a hardcoded list — a firm that adds "BPP" to its client
  // types gets it here without a code change, which is the whole point of the registry.
  const clientTypesQuery = useQuery<string[] | { client_types?: string[] }>({
    queryKey: ['org-settings', 'client-types'],
    queryFn: ({ signal }) => api.get('/api/org-settings/client-types', signal),
    staleTime: 10 * 60_000,
  });
  const clientCategoriesQuery = useQuery<string[] | { client_categories?: string[] }>({
    queryKey: ['org-settings', 'client-categories'],
    queryFn: ({ signal }) => api.get('/api/org-settings/client-categories', signal),
    staleTime: 10 * 60_000,
  });
  const registryOptions = (data: unknown, key: string): SelectOption[] => {
    const values = Array.isArray(data)
      ? data
      : ((data as Record<string, unknown>)?.[key] as string[] | undefined) ?? [];
    return values.map((v) => ({ value: v, label: v }));
  };
  const typeOptions = useMemo(
    () => registryOptions(clientTypesQuery.data, 'client_types'), [clientTypesQuery.data]);
  const categoryOptions = useMemo(
    () => registryOptions(clientCategoriesQuery.data, 'client_categories'), [clientCategoriesQuery.data]);

  // State filter options come from the canonical `state` enum surfaced by the
  // schema endpoint (the same 50+DC list every state-bearing select uses), so
  // the dropdown never drifts from the DB constraint.
  const schemaQuery = useQuery<SchemaResponse>({
    queryKey: ['clients-schema'],
    queryFn: ({ signal }) => api.get<SchemaResponse>('/api/schema/clients', signal),
    staleTime: 60 * 60_000,
  });
  const stateOptions = useMemo<SelectOption[]>(
    () => schemaQuery.data?.fields.find((f) => f.name === 'state')?.enumOptions ?? [],
    [schemaQuery.data],
  );

  // Current user powers the SavedViewMenu's share/delete affordances.
  const meQuery = useQuery<CurrentUser>({
    queryKey: ['current-user'],
    queryFn: ({ signal }) => api.get<CurrentUser>('/api/current-user', signal),
    staleTime: 5 * 60_000,
  });
  const canShare = ADMIN_TIER.has(meQuery.data?.role ?? '');

  // The current filter/sort envelope a saved view stores. Applying a view writes
  // these back onto the server-side list state below.
  const currentConfig = useMemo(
    () => ({
      q: list.searchInput,
      status: statusFilter,
      state: stateFilter,
      sort: list.sorting[0]?.id ?? 'name',
      dir: list.sorting[0]?.desc ? 'desc' : 'asc',
    }),
    [list.searchInput, statusFilter, stateFilter, list.sorting],
  );

  function applyView(config: Record<string, unknown>) {
    list.setSearchInput(typeof config.q === 'string' ? config.q : '');
    setStatusFilter(typeof config.status === 'string' ? config.status : '');
    setStateFilter(typeof config.state === 'string' ? config.state : '');
    const sortId = typeof config.sort === 'string' ? config.sort : 'name';
    list.setSorting([{ id: sortId, desc: config.dir === 'desc' }]);
    // useServerList resets to page 1 whenever search/filter/sort change.
  }

  const columns = useMemo<ColumnDef<Client>[]>(() => [
    {
      accessorKey: 'name',
      header: 'Name',
      cell: ({ row, getValue }) => (
        <div>
          <div style={{ fontWeight: 'var(--fw-medium)', color: 'var(--color-ink)' }}>
            {getValue() as string}
          </div>
          {row.original.short_name && row.original.short_name !== row.original.name && (
            <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-ink-3)' }}>
              {row.original.short_name}
            </div>
          )}
        </div>
      ),
    },
    {
      // Clickable state: one click narrows the whole list to that state
      // (mirrors the parcels clickable-state affordance). stopPropagation so it
      // filters instead of opening the row.
      accessorKey: 'state',
      header: 'State',
      cell: ({ getValue }) => {
        const s = getValue() as string | null;
        if (!s) return <span style={{ color: 'var(--color-ink-3)' }}>—</span>;
        return (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setStateFilter(s); }}
            title={`Show only ${s} clients`}
            style={{
              border: 'none',
              background: 'transparent',
              padding: 0,
              color: 'var(--brand-primary, var(--color-ink))',
              fontFamily: 'inherit',
              fontSize: 'inherit',
              fontWeight: 'var(--fw-medium)',
              cursor: 'pointer',
            }}
          >
            {s}
          </button>
        );
      },
    },
    { accessorKey: 'category', header: 'Category', cell: ({ getValue }) => (getValue() as string) || '—' },
    // Contract / Expires columns removed with migration 006 — they were an
    // engagement contract for a property-tax consultancy, empty on all 3,293
    // businesses, and rendered "—" in every row of the customer's own register.
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ getValue }) => {
        const s = getValue() as Client['status'];
        return <Badge variant={statusVariant[s]} dot>{statusLabel[s]}</Badge>;
      },
    },
  ], []);

  const { query, rows, total } = list;
  const hasFilters = list.search !== '' || statusFilter !== '' || stateFilter !== '';

  return (
    <>
      <PageHeader
        title="Businesses"
        subtitle={query.isLoading ? 'Loading…' : `${total.toLocaleString()} ${total === 1 ? 'business' : 'businesses'} in the register`}
        actions={
          <>
            <SavedViewMenu
              entityType="clients"
              currentConfig={currentConfig}
              onApply={applyView}
              canShare={canShare}
              currentUserId={meQuery.data?.id}
            />
            <Button variant="primary" size="md" onClick={() => navigate('/clients/new')}>
              + New business
            </Button>
          </>
        }
      />

      <ListToolbar
        searchValue={list.searchInput}
        onSearchChange={list.setSearchInput}
        searchPlaceholder="Search businesses by name, city, state, sector…"
        filters={[
          {
            key: 'status',
            value: statusFilter,
            onChange: setStatusFilter,
            placeholder: 'All statuses',
            options: STATUS_OPTIONS,
          },
          {
            key: 'state',
            value: stateFilter,
            onChange: setStateFilter,
            placeholder: 'All states',
            options: stateOptions,
            widthPx: 150,
          },
          {
            key: 'type',
            value: typeFilter,
            onChange: setTypeFilter,
            placeholder: 'All business types',
            options: typeOptions,
          },
          {
            key: 'category',
            value: categoryFilter,
            onChange: setCategoryFilter,
            placeholder: 'All categories',
            options: categoryOptions,
          },
        ]}
      />

      {query.error && (
        <div style={{
          padding: 'var(--sp-4)',
          background: 'var(--color-status-red-bg)',
          border: '1px solid var(--color-status-red)',
          color: 'var(--color-status-red)',
          borderRadius: 'var(--r-lg)',
          marginBottom: 'var(--sp-4)',
        }}>
          <strong>Failed to load businesses:</strong> {(query.error as Error).message}
        </div>
      )}

      {!query.isLoading && total === 0 && !query.error && (
        hasFilters ? <NoMatches /> : <EmptyState />
      )}

      {(total > 0 || query.isFetching) && !query.error && (
        <>
          <DataTable
            data={rows}
            columns={columns}
            sorting={list.sorting}
            onSortingChange={list.setSorting}
            onRowClick={(row) => navigate(`/clients/${row.id}`)}
            // Warm the client DETAIL query on row hover so the click opens a
            // cached page. ClientDetail reads the BFF view (['client-view', id] →
            // /api/views/client/:id), so prefetch THAT exact key/endpoint.
            onRowPrefetch={(row) => {
              void queryClient.prefetchQuery({
                queryKey: ['client-view', row.id],
                queryFn: ({ signal }) => api.get(`/api/views/client/${row.id}`, signal),
              });
            }}
            emptyMessage={query.isFetching ? 'Loading…' : 'No clients'}
          />
          <Pagination
            pageIndex={list.pageIndex}
            pageCount={list.pageCount}
            pageSize={list.pageSize}
            totalRows={total}
            onPageChange={list.setPageIndex}
            onPageSizeChange={list.setPageSize}
          />
        </>
      )}
    </>
  );
}

function NoMatches() {
  return (
    <div style={{
      padding: 'var(--sp-7)',
      background: 'var(--color-card)',
      border: '1px dashed var(--color-border)',
      borderRadius: 'var(--r-lg)',
      color: 'var(--color-muted)',
      textAlign: 'center',
    }}>
      <p style={{ margin: 0, fontSize: 'var(--fs-md)' }}>No businesses match your search or filters.</p>
    </div>
  );
}

function EmptyState() {
  return (
    <div style={{
      padding: 'var(--sp-8)',
      background: 'var(--color-card)',
      border: '1px dashed var(--color-border)',
      borderRadius: 'var(--r-lg)',
      color: 'var(--color-muted)',
      textAlign: 'center',
    }}>
      <h2 style={{
        margin: '0 0 var(--sp-2)',
        fontSize: 'var(--fs-xl)',
        fontWeight: 'var(--fw-semibold)',
        color: 'var(--color-ink-3)',
      }}>No businesses yet</h2>
      <p style={{ margin: 0, fontSize: 'var(--fs-md)' }}>
        Use “+ New business” to register the first one, or bring a batch in from a CSV.
      </p>
    </div>
  );
}
